import React, { useState } from "react";
import toast from "react-hot-toast";
// The standard "send email with attachments" modal — same one the Documents
// tab uses when a GP selects rows via checkbox and sends them by email.
import { SendEmailModal } from "pages/patient/PatientDocumentsPage/components/SendEmailModal/SendEmailModal";

import { ConfirmModal } from "components/helpers/ConfirmModal/ConfirmModal";
import { DataTable } from "components/ui/DataTable";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";

import { FILTERS_CONFIG, formatCurrency, formatDate, TAB_OPTIONS } from "./constants.jsx";
import { useCreatePaymentEmailAttachment } from "./hooks/useCreatePaymentEmailAttachment";
import { usePaymentActions } from "./hooks/usePaymentActions";
import { usePaymentColumns } from "./hooks/usePaymentColumns.jsx";
import { usePaymentsData } from "./hooks/usePaymentsData";
import styles from "./PatientPaymentsPage.module.css";

const PatientPaymentsPage = () => {
  const patientId = useCurrentPatientId();
  const { data: patient } = usePatientData(patientId);

  const [pendingPayment, setPendingPayment] = useState(null);
  const [emailAttachment, setEmailAttachment] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const {
    activeTab,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    showAllPaymentsDue,
    setShowAllPaymentsDue,
    filteredPayments,
    isLoading,
    totalRecords,
    handleTabChange,
    selection,
  } = usePaymentsData(patientId);

  const {
    handlePaymentConfirmation,
    handleDownloadReceipt,
    handlePrintStatement,
  } = usePaymentActions(patientId);
  const createEmailAttachment = useCreatePaymentEmailAttachment(patientId);

  const handleSendEmail = (paymentIds, attachmentLabel) => {
    if (createEmailAttachment.isPending) return;

    toast.loading("Preparing attachment...", { id: "payment-email-attachment" });
    createEmailAttachment.mutate(
      { paymentIds, name: `${attachmentLabel} - ${formatDate(new Date())}` },
      {
        onSuccess: (document) => {
          toast.dismiss("payment-email-attachment");
          setEmailAttachment(document);
          setIsEmailModalOpen(true);
        },
        onError: () => {
          toast.dismiss("payment-email-attachment");
          toast.error("Failed to prepare the attachment. Please try again.");
        },
      }
    );
  };

  const { columns, rowActions, bulkActions } = usePaymentColumns(activeTab, {
    onPay: (payment) => setPendingPayment(payment),
    onDownloadReceipt: handleDownloadReceipt,
    onPrintStatement: handlePrintStatement,
    onSendEmail: handleSendEmail,
  });

  return (
    <SimplePatientPageLayout>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <h3 className={styles.title}>Payment Details</h3>
          <TabSwitcher
            value={activeTab}
            onChange={handleTabChange}
            options={TAB_OPTIONS}
            fullWidth
          />
        </div>
        {activeTab === "paymentsDue" && (
          <label className={styles.dueFilterRow}>
            <span className={styles.dueFilterLabel}>
              Show all (including €0 due)
            </span>
            <span className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={showAllPaymentsDue}
                onChange={(e) => setShowAllPaymentsDue(e.target.checked)}
              />
              <span className={styles.toggleSlider} />
            </span>
          </label>
        )}
        <DataTable
          columns={columns}
          data={filteredPayments}
          isLoading={isLoading}
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            placeholder: "Search service or date...",
          }}
          filters={{
            value: { startDate, endDate },
            onChange: (groupId, value) => {
              if (groupId === "startDate") {
                setStartDate(value || "");
                setCurrentPage(1);
              } else if (groupId === "endDate") {
                setEndDate(value || "");
                setCurrentPage(1);
              }
            },
          }}
          filtersConfig={FILTERS_CONFIG}
          selectable
          selection={selection}
          rowActions={rowActions}
          bulkActions={bulkActions}
          currentPage={currentPage}
          totalCount={totalRecords}
          rowsPerPage={rowsPerPage}
          rowsOptions={[10, 20, 50, 100]}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
          emptyTitle="No payment details found"
          emptyText="No payment records are available at this time."
        />
      </div>

      <ConfirmModal
        isOpen={!!pendingPayment}
        onClose={() => setPendingPayment(null)}
        onConfirm={() => {
          handlePaymentConfirmation(pendingPayment);
          setPendingPayment(null);
        }}
        title="Confirm Payment"
        message={
          <>
            Confirm payment of <strong>{formatCurrency(pendingPayment?.amountDue)}</strong> for {pendingPayment?.practiceService?.name || pendingPayment?.serviceName || "this service"}?
          </>
        }
        confirmLabel="Pay"
      />

      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setEmailAttachment(null);
          selection.deselectAll();
        }}
        patient={patient}
        documents={emailAttachment ? [emailAttachment] : []}
      />
    </SimplePatientPageLayout>
  );
};

export { PatientPaymentsPage };
